import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { RecipeSummary } from '@recipes/contracts';
import { Button, cn } from '@recipes/ui';
import { GripVertical, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CompactRecipeCard } from '@/features/shared/recipe-card';

type Item = RecipeSummary & { position: number };

/**
 * Drag-and-drop reorderable recipe grid with keyboard support (space to pick up,
 * arrows to move, space to drop) and live announcements.
 */
export function SortableRecipeGrid({
  recipes,
  canEdit,
  onReorder,
  onRemove,
}: {
  recipes: Item[];
  canEdit: boolean;
  onReorder: (recipeIds: string[]) => void;
  onRemove: (recipeId: string) => void;
}) {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = recipes.map((r) => r.id);
  const titleOf = (id: string | number | undefined) =>
    recipes.find((r) => r.id === id)?.title ?? '';

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(ids, from, to));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      accessibility={{
        screenReaderInstructions: { draggable: t('collections.dnd.instructions') },
        announcements: {
          onDragStart: ({ active }) => t('collections.dnd.pickedUp', { title: titleOf(active.id) }),
          onDragOver: ({ active, over }) =>
            over
              ? t('collections.dnd.over', {
                  title: titleOf(active.id),
                  position: ids.indexOf(String(over.id)) + 1,
                })
              : '',
          onDragEnd: ({ active, over }) =>
            over
              ? t('collections.dnd.dropped', {
                  title: titleOf(active.id),
                  position: ids.indexOf(String(over.id)) + 1,
                })
              : t('collections.dnd.cancelled'),
          onDragCancel: ({ active }) =>
            t('collections.dnd.cancelled', { title: titleOf(active.id) }),
        },
      }}
    >
      <SortableContext items={ids} strategy={rectSortingStrategy} disabled={!canEdit}>
        <ul
          className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4"
          aria-label={t('collections.recipes')}
        >
          {recipes.map((recipe) => (
            <SortableCard
              key={recipe.id}
              recipe={recipe}
              canEdit={canEdit}
              onRemove={() => onRemove(recipe.id)}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function SortableCard({
  recipe,
  canEdit,
  onRemove,
}: {
  recipe: Item;
  canEdit: boolean;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: recipe.id, disabled: !canEdit });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <li ref={setNodeRef} style={style} className={cn(isDragging && 'z-10 opacity-80')}>
      <CompactRecipeCard
        recipe={recipe}
        actions={
          canEdit ? (
            <>
              <Button
                ref={setActivatorNodeRef}
                type="button"
                variant="ghost"
                size="icon"
                className="cursor-grab touch-none"
                aria-label={t('collections.dnd.handle', { title: recipe.title })}
                {...attributes}
                {...listeners}
              >
                <GripVertical aria-hidden />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('collections.removeRecipe', { title: recipe.title })}
                onClick={onRemove}
              >
                <X aria-hidden />
              </Button>
            </>
          ) : undefined
        }
      />
    </li>
  );
}
